// The widget's post-action rating ask: after a happy rating (4–5★) the free
// text answers "How has {product} made your life better?" ("have" for plural
// names like Rose Yummies) and travels as
// `life_better` — stored apart from reviews. Below that it stays the ordinary
// review note (`comment`). Widget-only, so the shared rate-products helpers
// (and the web app's payload) stay exactly as they are.

import {
  buildRatingResponses,
  type RatingResponse,
  type RatingValue,
} from "shared/rate-products";

/** Lowest rating that turns the note into the life-better question. */
export const LIFE_BETTER_MIN_RATING: RatingValue = 4;

export function asksLifeBetter(rating: RatingValue | null | undefined): boolean {
  return rating != null && rating >= LIFE_BETTER_MIN_RATING;
}

/**
 * Whether a product name reads as plural ("Rose Yummies", "Mist Refills"):
 * the name before any " - ", " | " or " (" variant suffix, minus a trailing
 * "Auto renew", ends in a single "s".
 */
export function isPluralTitle(title: string): boolean {
  const base = title
    .split(/\s+[|\-–(]/)[0]
    .replace(/\s+auto\s+renew$/i, "")
    .trim();
  const last = base.split(/\s+/).pop() ?? "";
  return /[^s']s$/i.test(last);
}

export function lifeBetterQuestion(title: string): string {
  return `How ${isPluralTitle(title) ? "have" : "has"} ${title} made your life better?`;
}

/** A rated product as the widget submits it: a review note or a life-better answer, never both. */
export interface WidgetRatingResponse {
  productId: number;
  rating: RatingValue;
  comment?: string;
  lifeBetter?: string;
}

/**
 * The shared builder (trim, clip, drop blanks), then the typed text goes
 * where the final rating says: `lifeBetter` at 4–5★, `comment` below.
 */
export function buildWidgetRatingResponses(
  ratings: Record<number, RatingValue>,
  notes: Record<number, string>,
): WidgetRatingResponse[] {
  return buildRatingResponses(ratings, notes).map(
    ({ comment, ...rest }: RatingResponse): WidgetRatingResponse => {
      if (!comment) return rest;
      return asksLifeBetter(rest.rating)
        ? { ...rest, lifeBetter: comment }
        : { ...rest, comment };
    },
  );
}
